/**
 * store/session.ts — whether we are in, and what to draw while we find out.
 *
 * Four states and no more:
 *
 * | состояние | что видно |
 * |---|---|
 * | `loading` | заставка, пока идёт первый запрос |
 * | `ready` | приложение |
 * | `not-subscribed` | экран «подпишитесь» |
 * | `failed` | одна фраза и кнопка «Ещё раз» |
 *
 * `not-subscribed` is its own state rather than a kind of `failed` because nothing is
 * broken: the app is working exactly as configured, and the screen for it has a way out
 * that no error screen has.
 *
 * ## Why «Я подписался» is the same call as the first attempt
 *
 * It re-runs the login. The server never caches a refusal, so the answer is truthful the
 * moment the person has joined — which is the whole reason the negative verdict is not
 * cached on the server side. **No page reload:** a reload throws away the launch data and on
 * a slow connection looks like the app crashed.
 *
 * ## The pass is kept alive (task 43)
 *
 * The pass lives six hours, and an app left open longer used to fail quietly after that: the
 * queue stopped being saved, hearts stopped landing, a fresh sound link ended in «Повторить»
 * (`.omc/wiki/dolgi-posle-zadachi-08.md`). Now, once signed in:
 *
 * - **Renewed ahead of time.** A plain interval looks every {@link RENEW_CHECK_MS} whether the
 *   pass has {@link RENEW_LEAD_MS} or less to go, and if so asks `POST /api/auth/refresh`; the
 *   new pass and its time replace the old ones, and the next renewal is counted from them. An
 *   interval and not one timer for the whole six hours: a phone that slept through the moment
 *   wakes up with the check still due, and a renewal that failed is asked again next time.
 * - **Signed in again on any `401`.** `lib/api.ts` hands a refused pass to {@link reauthorize}:
 *   the launch data are still in the page, the sign-in runs once more in silence — the screen
 *   stays the app, not «Открываю плеер…» — and the request is sent once more with the new pass.
 *   Several requests refused at once wait on the same sign-in.
 *
 * Nothing here touches the sound: no `play()`, no pause, nothing of the player's.
 */

import { create } from 'zustand';

import { login, onUnauthorized, refreshSession } from '../lib/api';
import type { ApiFailure, SessionAnswer } from '../lib/api';

/** The pass is renewed when it has this much or less to go. */
export const RENEW_LEAD_MS = 5 * 60_000;

/** How often the interval looks whether the pass is due. */
export const RENEW_CHECK_MS = 30_000;

export type SessionStatus = 'loading' | 'ready' | 'not-subscribed' | 'failed';

export interface SessionState {
  readonly status: SessionStatus;
  readonly session: SessionAnswer | undefined;
  readonly failure: ApiFailure | undefined;
  /** The channel link the «subscribe» screen puts on its button, when the server sent one. */
  readonly joinUrl: string | undefined;
  /**
   * Runs the launch. Safe to call again — that is «Я подписался».
   *
   * A property with a function type, not a method: a component selects it out of the store
   * and calls it detached from the object, which is exactly what `unbound-method` warns
   * about for a method declaration.
   */
  readonly signIn: (initData: string) => Promise<void>;
  /**
   * Task 43: signs in again in silence with the launch data of the last {@link signIn}, and
   * answers the new pass — or `undefined` when there is none to be had. The screen does not
   * leave the app for it.
   */
  readonly reauthorize: () => Promise<string | undefined>;
}

/** Pulls `joinUrl` out of the envelope's details without trusting its shape. */
function joinUrlOf(failure: ApiFailure): string | undefined {
  const value = failure.details?.['joinUrl'];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * One launch at a time.
 *
 * Two callers can ask at once and neither is a bug: `StrictMode` double-invokes the mount
 * effect in development, and a person can tap «Я подписался» twice before the first answer
 * lands. Both would otherwise cost a second `getChatMember` and could settle out of order,
 * so the second caller waits on the first instead of starting its own.
 *
 * Module-level rather than in the store: it is not state anything renders, and putting it in
 * the store would make every subscriber re-render when it changes.
 */
let inFlight: Promise<void> | undefined;

/** The launch data of the last sign-in: what a silent sign-in sends again. */
let launchData: string | undefined;

/** The silent sign-in in flight, which every refused request waits on. */
let reauthorizing: Promise<string | undefined> | undefined;

/** The renewal interval, once signed in; one for the life of the page. */
let renewal: ReturnType<typeof setInterval> | undefined;

/** A renewal in flight: the interval does not ask twice. */
let renewing = false;

export const useSession = create<SessionState>((set, get) => ({
  status: 'loading',
  session: undefined,
  failure: undefined,
  joinUrl: undefined,

  signIn: async (initData: string): Promise<void> => {
    const running = inFlight;
    if (running !== undefined) {
      await running;
      return;
    }
    launchData = initData;
    const attempt = runSignIn(initData, set);
    inFlight = attempt;
    try {
      await attempt;
    } finally {
      inFlight = undefined;
    }
    if (get().status === 'ready') keepRenewed();
  },

  reauthorize: async (): Promise<string | undefined> => {
    reauthorizing ??= silentSignIn(set).finally(() => {
      reauthorizing = undefined;
    });
    return await reauthorizing;
  },
}));

/** A sign-in that leaves the screen alone: the new pass, or `undefined`. */
async function silentSignIn(
  set: (partial: Partial<SessionState>) => void,
): Promise<string | undefined> {
  if (launchData === undefined) return undefined;
  const result = await login(launchData);
  if (!result.ok) return undefined;
  set({ session: result.data });
  return result.data.token;
}

/** Asks for a fresh pass when the one held is due. See «The pass is kept alive». */
async function renewIfDue(): Promise<void> {
  const session = useSession.getState().session;
  if (session === undefined || renewing) return;
  if (Date.now() < session.expiresAt * 1000 - RENEW_LEAD_MS) return;
  renewing = true;
  try {
    const result = await refreshSession(session.token);
    if (result.ok) {
      // The refresh carries no launch: the profile and the payload stay the launch's.
      const current = useSession.getState().session ?? session;
      useSession.setState({
        session: { ...current, token: result.data.token, expiresAt: result.data.expiresAt },
      });
    } else if (result.error.status === 401) {
      await useSession.getState().reauthorize();
    }
  } finally {
    renewing = false;
  }
}

function keepRenewed(): void {
  if (renewal !== undefined) return;
  renewal = setInterval(() => {
    void renewIfDue();
  }, RENEW_CHECK_MS);
}

/** Stops the renewal and forgets the launch — the web suite's seam between cases. */
export function resetSessionKeeping(): void {
  if (renewal !== undefined) clearInterval(renewal);
  renewal = undefined;
  renewing = false;
  launchData = undefined;
  reauthorizing = undefined;
}

onUnauthorized(async () => await useSession.getState().reauthorize());

/** The launch itself. Split out so the guard above reads as a guard and nothing else. */
async function runSignIn(
  initData: string,
  set: (partial: Partial<SessionState>) => void,
): Promise<void> {
  {
    set({ status: 'loading', failure: undefined });
    const result = await login(initData);

    if (result.ok) {
      set({ status: 'ready', session: result.data, failure: undefined, joinUrl: undefined });
      return;
    }

    set({
      status: result.error.code === 'NOT_SUBSCRIBED' ? 'not-subscribed' : 'failed',
      session: undefined,
      failure: result.error,
      joinUrl: joinUrlOf(result.error),
    });
  }
}
